import type {
  AiRiskUiBand,
  AiSuggestionDetail,
  ContentRiskLevel,
} from "../types/aiSuggestion";

const DEFAULT_CONTENT_RISK_LEVEL: ContentRiskLevel = "BusinessDecision";

const LEVEL_ALIASES: Readonly<Record<string, ContentRiskLevel>> = {
  legaldecision: "LegalDecision",
  businessdecision: "BusinessDecision",
  execdecision: "ExecDecision",
};

export const getDefaultContentRiskLevel = (): ContentRiskLevel =>
  DEFAULT_CONTENT_RISK_LEVEL;

/** Case-insensitive match to a known {@link ContentRiskLevel}, or null. */
export const parseContentRiskLevel = (
  raw: string | null | undefined,
): ContentRiskLevel | null => {
  if (raw == null || raw === "") {
    return null;
  }
  const key = raw.trim().toLowerCase();
  return LEVEL_ALIASES[key] ?? null;
};

export const contentRiskLevelToUiBand = (
  level: ContentRiskLevel,
): AiRiskUiBand => {
  switch (level) {
    case "LegalDecision":
      return "legal";
    case "BusinessDecision":
      return "business";
    case "ExecDecision":
      return "exec";
  }
};

/** Map API row `content_risk_level` string to UI band (defaults to legal when unknown). */
export const rowContentRiskToUiBand = (
  raw: string | null | undefined,
): AiRiskUiBand => {
  const parsed = parseContentRiskLevel(raw ?? null);
  return parsed ? contentRiskLevelToUiBand(parsed) : "legal";
};

/** Static display percent per product spec (FR-004). */
export const staticRiskPercentLabel = (
  band: AiRiskUiBand,
): "90%" | "85%" | "95%" => {
  switch (band) {
    case "legal":
      return "90%";
    case "business":
      return "85%";
    case "exec":
      return "95%";
    default:
      return "90%";
  }
};

export const displayOriginalText = (
  text: string | null | undefined,
): string => {
  if (text == null || text.trim() === "") {
    return "—";
  }
  return text;
};

/** Prefer reason as card title; fall back to original snippet. */
export const suggestionCardTitle = (args: {
  readonly reason: string | null | undefined;
  readonly original_text: string | null | undefined;
}): string => {
  const r = args.original_text?.trim();
  if (r) {
    return r;
  }
  return displayOriginalText(args.original_text);
};

/** Formats API confidence (0–1 or 0–100) for badge display. */
export const formatConfidencePercent = (
  confidence: number | null | undefined,
): string | null => {
  if (confidence == null || Number.isNaN(confidence)) {
    return null;
  }
  const n =
    confidence <= 1 && confidence >= 0
      ? Math.round(confidence * 100)
      : Math.round(confidence);
  return `${n}%`;
};

export const displaySectionSubtitle = (
  sectionName: string | null | undefined,
): string | null => {
  if (sectionName == null || sectionName.trim() === "") {
    return null;
  }
  return sectionName.trim();
};

/**
 * Stable id for list keys and `activeClauseId` (FR-013).
 * Prefer non-empty `suggestion_item_id`; otherwise a deterministic composite for the row.
 */
export const stableSuggestionRowId = (
  suggestion: Pick<
    AiSuggestionDetail,
    | "content_risk_level"
    | "section_index"
    | "content_index"
    | "suggestion_item_id"
  >,
  listIndex: number,
): string => {
  const sid = suggestion.suggestion_item_id?.trim();
  if (sid) {
    return sid;
  }
  return `${parseContentRiskLevel(suggestion.content_risk_level) ?? "x"}-${listIndex}-${suggestion.section_index ?? "s"}-${suggestion.content_index ?? "c"}`;
};
